import React from 'react';
import PropTypes from 'prop-types';
import {TextInput} from '@/components/atoms';
import {preventInvalidNumericInput} from '@/utils/inputValidations';
import {REWARD_TYPES} from '@/utils/constants/rewardsConstants';
import {Constants} from '@/utils/constants/commonConstants';
import styles from './InventoryFieldsRow.module.scss';

/**
 * InventoryFieldsRow component - Reusable component for quota, leftover inventory, and limit per store fields
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data containing quota, leftOverInventory, limitPerStore
 * @param {boolean} props.isQuotaDisabled - Whether quota field should be disabled
 * @param {Function} props.handleQuotaChange - Function to handle quota field changes
 * @param {Function} props.handleLeftOverInventoryChange - Function to handle leftover inventory field changes
 * @param {Function} props.handleLimitPerStoreChange - Function to handle limit per store field changes
 * @param {Function} props.handleMaxQtyPerOrderChange - Function to handle max quantity per order field changes
 * @param {string} props.quotaValidationError - Validation error message for quota field
 * @param {string} props.limitValidationError - Validation error message for limit per store field
 * @param {string} props.maxQtyPerOrderValidationError - Validation error message for max quantity per order field
 * @returns {JSX.Element} The rendered InventoryFieldsRow component
 */
const InventoryFieldsRow = ({
  formData,
  isQuotaDisabled,
  handleQuotaChange,
  handleLeftOverInventoryChange,
  handleLimitPerStoreChange,
  handleMaxQtyPerOrderChange,
  quotaValidationError,
  limitValidationError,
  maxQtyPerOrderValidationError
}) => {
  const isPepVoucherDiscount =
    formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_DISCOUNT;
  const isPepVoucherProduct =
    formData?.rewardType === REWARD_TYPES.PEP_VOUCHER_PRODUCT;
  const showMaxQuantityPerOrder = isPepVoucherDiscount || isPepVoucherProduct;
  const renderTextInput = ({
    id,
    label,
    value,
    onChange,
    placeholderText,
    className = '',
    isDark = true,
    type = 'text',
    disabled = false,
    handleKeyDown = preventInvalidNumericInput,
    maxLength,
    error
  }) => (
    <div className='w-full md:w-[262px]'>
      <TextInput
        id={id}
        label={label}
        value={value}
        onChange={onChange}
        placeholderText={placeholderText}
        className={`w-full text-sm placeholder:text-sm ${className}`}
        isDark={isDark}
        type={type}
        disabled={disabled}
        handleKeyDown={handleKeyDown}
        maxLength={maxLength}
        error={error}
      />
    </div>
  );

  return (
    <div className='flex flex-col items-start gap-6 md:flex-row'>
      {renderTextInput({
        id: 'quota',
        label: 'Quota*',
        value: formData?.unlimitedInventory
          ? Constants.Unlimited
          : formData?.quota || '',
        onChange: handleQuotaChange,
        placeholderText: 'Add Quota',
        className: styles.inventoryFieldsRow__quota,
        disabled: isQuotaDisabled,
        maxLength: 6,
        error: quotaValidationError
      })}

      {renderTextInput({
        id: 'add-inventory',
        label: 'Leftover Inventory',
        value: formData?.leftOverInventory || '',
        onChange: handleLeftOverInventoryChange,
        placeholderText: 'Leftover Inventory',
        className: styles.inventoryFieldsRow__leftoverInventory,
        disabled: true
      })}

      {renderTextInput({
        id: 'limit-per-store',
        label: 'Limit per Store',
        value: formData?.limitPerStore || '',
        onChange: handleLimitPerStoreChange,
        placeholderText: 'Limit Account',
        className: styles.inventoryFieldsRow__limitPerStore,
        maxLength: 3,
        error: limitValidationError
      })}

      {showMaxQuantityPerOrder &&
        renderTextInput({
          id: 'max-quantity-per-order',
          label: 'Max Quantity per Order*',
          value: formData?.maxQtyPerOrder || '',
          onChange: handleMaxQtyPerOrderChange,
          placeholderText: 'Max Quantity per Order',
          className: styles.inventoryFieldsRow__limitPerStore,
          maxLength: 6,
          error: maxQtyPerOrderValidationError
        })}
    </div>
  );
};

export default InventoryFieldsRow;

InventoryFieldsRow.propTypes = {
  formData: PropTypes.object.isRequired,
  isQuotaDisabled: PropTypes.bool.isRequired,
  handleQuotaChange: PropTypes.func.isRequired,
  handleLeftOverInventoryChange: PropTypes.func.isRequired,
  handleLimitPerStoreChange: PropTypes.func.isRequired,
  handleMaxQtyPerOrderChange: PropTypes.func.isRequired,
  quotaValidationError: PropTypes.string,
  limitValidationError: PropTypes.string,
  maxQtyPerOrderValidationError: PropTypes.string
};
